import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Popper
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { createEvisDeployment, createChannel } from '../../utilities/mezzanineApi';
import { useMezzanine } from '../../context/MezzanineContext';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '& .MuiInputBase-root': { fontSize: '12px' },
    '& .MuiInputLabel-root': { fontSize: '12px' },
    '& .MuiMenuItem-root': { fontSize: '12px' },
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  chip: { margin: 2, fontSize: '11px' },
  // The option list renders in a portal, so it sits outside `form` and needs
  // its own font size to match the rest of the dialog. maxHeight is capped
  // because the default 40vh is tall enough that Popper slides the list up over
  // the input to keep it inside the viewport.
  autocompleteListbox: {
    fontSize: '12px',
    // Generous, so a wide match set is readable. When this does not fit below
    // the field the popper flips above it rather than being squeezed.
    maxHeight: '40vh',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f0f0f0',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ff6f00',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#e65100',
      },
    },
  },
}));

// `flip` and `preventOverflow` solve the same problem in opposite ways:
//   flip           - moves the list wholly above the field. No overlap.
//   preventOverflow - slides the list to stay on screen, which drags it over
//                     the input you are typing in.
// So flip stays on and preventOverflow stays off: the list opens downward when
// there is room and upward when there is not, always at full height and never
// covering the field.
const AutocompletePopper = (props) => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={{
      flip: { enabled: true },
      preventOverflow: { enabled: false },
      hide: { enabled: false },
    }}
  />
);

const REGIONS = ['lhr', 'fra', 'iad', 'pdx'];
const PRIORITIES = ['prim', 'secd'];
const DEPLOYMENT_TYPES = [
  { value: 'deployment', label: 'EVIS Full Channel Deployment' },
  { value: 'endpoint', label: 'EVIS Single End-Point Deployment' },
  { value: 'fanout', label: 'EVIS Fanout Channel' },
];

// 'endpoint' and 'fanout' both POST to /channels/create and share the same
// inputs, so they share channelForm state and the data-center -> TX-chain
// cascade. fanout additionally sends fanout: true and MUST omit region and
// stream_priority - the API returns 400 if either is present.

const CreateDeploymentForm = ({ open, onClose, onSuccess, onAutoDeployStart, onNotify }) => {
  const classes = useStyles();
  const { programGroups, programGroupsLoading, dataCenters, dataCentersLoading, txChains } = useMezzanine();
  
  const [deploymentType, setDeploymentType] = useState('deployment');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [deploymentForm, setDeploymentForm] = useState({
    eid: '', group_name: '', data_centers: [], txchain_number: '', auto_deploy: false, create_udp: false,
  });

  const [channelForm, setChannelForm] = useState({
    eid: '', group_name: '', data_center: '', tx_chain_name: '', region: '', stream_priority: '', auto_deploy: false, create_udp: false,
  });

  // Sorted so the searchable list is predictable; ~58 groups in prod today.
  const programGroupNames = useMemo(
    () =>
      programGroups
        .map((group) => group.group_name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [programGroups]
  );

  const txChainNumbersWithStatus = useMemo(() => {
    const selectedDCs = deploymentForm.data_centers;
    if (selectedDCs.length !== 2) return [];
    
    // Filter txChains to only selected data centers
    const relevantChains = txChains.filter(tc => selectedDCs.includes(tc.data_center));
    
    // Group by tx_chain_number
    const chainsByNumber = {};
    relevantChains.forEach(tc => {
      if (!chainsByNumber[tc.tx_chain_number]) {
        chainsByNumber[tc.tx_chain_number] = [];
      }
      chainsByNumber[tc.tx_chain_number].push(tc);
    });
    
    // Include numbers that exist in both DCs, mark busy ones
    return Object.entries(chainsByNumber)
      .filter(([num, chains]) => {
        const dcsWithThisNumber = new Set(chains.map(tc => tc.data_center));
        return selectedDCs.every(dc => dcsWithThisNumber.has(dc));
      })
      .map(([num, chains]) => ({
        number: parseInt(num),
        busy: chains.some(tc => tc.available === false),
      }))
      .sort((a, b) => a.number - b.number);
  }, [deploymentForm.data_centers, txChains]);

  // TX chain names for single endpoint (all in selected DC, with busy indicator)
  const txChainNamesWithStatus = useMemo(() => {
    const selectedDC = channelForm.data_center;
    if (!selectedDC) return [];
    
    return txChains
      .filter(tc => tc.data_center === selectedDC)
      .map(tc => ({ name: tc.tx_chain_name, busy: !tc.available }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [channelForm.data_center, txChains]);

  const handleDeploymentChange = (field) => (event) => {
    if (field === 'data_centers') {
      const value = event.target.value;
      if (value.length > 2) return;
      setDeploymentForm(prev => ({ ...prev, data_centers: value, txchain_number: '' }));
      return;
    }
    const value = field === 'auto_deploy' || field === 'create_udp' ? event.target.checked : event.target.value;
    setDeploymentForm({ ...deploymentForm, [field]: value });
  };

  const handleChannelChange = (field) => (event) => {
    if (field === 'data_center') {
      const value = event.target.value;
      setChannelForm(prev => ({ ...prev, data_center: value, tx_chain_name: '' }));
      return;
    }
    const value = field === 'auto_deploy' || field === 'create_udp' ? event.target.checked : event.target.value;
    setChannelForm({ ...channelForm, [field]: value });
  };


  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      const isAutoDeploy = deploymentType === 'deployment'
        ? deploymentForm.auto_deploy
        : channelForm.auto_deploy;

      if (deploymentType === 'deployment') {
        const payload = {
          eid: deploymentForm.eid,
          group_name: deploymentForm.group_name,
          data_centers: deploymentForm.data_centers,
          txchain_number: parseInt(deploymentForm.txchain_number),
          auto_deploy: deploymentForm.auto_deploy,
          create_udp: deploymentForm.create_udp,
        };
        response = await createEvisDeployment(payload);
      } else if (deploymentType === 'endpoint') {
        const payload = {
          eid: channelForm.eid,
          group_name: channelForm.group_name,
          data_center: channelForm.data_center,
          tx_chain_name: channelForm.tx_chain_name,
          region: channelForm.region,
          stream_priority: channelForm.stream_priority,
          auto_deploy: channelForm.auto_deploy,
          create_udp: channelForm.create_udp,
        };
        response = await createChannel(payload);
      } else if (deploymentType === 'fanout') {
        // Creates ONE channel with one SRT output per EVIS ingress on the EID.
        // region and stream_priority are deliberately absent: the API rejects
        // them when fanout is true, and the resulting outputs carry their own.
        const payload = {
          eid: channelForm.eid,
          group_name: channelForm.group_name,
          data_center: channelForm.data_center,
          tx_chain_name: channelForm.tx_chain_name,
          fanout: true,
          auto_deploy: channelForm.auto_deploy,
          create_udp: channelForm.create_udp,
        };
        response = await createChannel(payload);
      } else {
        // Guards against a new entry in DEPLOYMENT_TYPES silently falling
        // through to the wrong endpoint.
        throw new Error(`Unsupported deployment type: ${deploymentType}`);
      }

      if (isAutoDeploy && onAutoDeployStart) {
        const executions = [];
        if (Array.isArray(response)) {
          response.forEach(item => {
            if (item.execution?.execution_arn && item.channel?.channel_id) {
              executions.push({ channelId: item.channel.channel_id, executionArn: item.execution.execution_arn });
            }
          });
        } else if (response.execution?.execution_arn && response.channel?.channel_id) {
          executions.push({ channelId: response.channel.channel_id, executionArn: response.execution.execution_arn });
        }
        if (executions.length > 0) onAutoDeployStart(executions);
      }

      const createdLabel = deploymentType === 'deployment'
        ? 'Deployment created'
        : deploymentType === 'fanout'
          ? 'Fanout channel created'
          : 'Channel created';
      const successMsg = `${createdLabel}${
        isAutoDeploy
          ? deploymentType === 'deployment'
            ? ' - deploying channels...'
            : ' - deploying...'
          : ''
      }`;
      if (onNotify) onNotify({ message: successMsg, severity: 'success' });
      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDeploymentForm({ eid: '', group_name: '', data_centers: [], txchain_number: '', auto_deploy: false, create_udp: false });
    setChannelForm({ eid: '', group_name: '', data_center: '', tx_chain_name: '', region: '', stream_priority: '', auto_deploy: false, create_udp: false });
    setError(null);
    onClose();
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ padding: '16px 20px', borderBottom: '1px solid #eee' }}>Create Channels</DialogTitle>
      <DialogContent>
        <Box className={classes.form} mt={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Type</InputLabel>
            <Select value={deploymentType} onChange={(e) => setDeploymentType(e.target.value)} label="Type">
              {DEPLOYMENT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value} style={{ fontSize: '12px' }}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && <Typography color="error" style={{ fontSize: '12px' }}>{error}</Typography>}

          {deploymentType === 'deployment' ? (
            <>
              <TextField label="EID" variant="outlined" size="small" fullWidth value={deploymentForm.eid} onChange={handleDeploymentChange('eid')} placeholder="amzn1.dv.live.eid.xxxxx" required />

              <Autocomplete
                options={programGroupNames}
                value={deploymentForm.group_name || null}
                onChange={(event, newValue) =>
                  setDeploymentForm((prev) => ({ ...prev, group_name: newValue || '' }))
                }
                disabled={programGroupsLoading}
                size="small"
                ListboxProps={{ className: classes.autocompleteListbox }}
                PopperComponent={AutocompletePopper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Program Group"
                    variant="outlined"
                    placeholder={programGroupsLoading ? 'Loading...' : 'Type to search'}
                    required
                  />
                )}
              />

              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Data Centers</InputLabel>
                <Select
                  multiple
                  value={deploymentForm.data_centers}
                  onChange={handleDeploymentChange('data_centers')}
                  label="Data Centers"
                  disabled={dataCentersLoading}
                  renderValue={(selected) => (
                    <div className={classes.chips}>
                      {selected.map((value) => (<Chip key={value} label={value} size="small" className={classes.chip} />))}
                    </div>
                  )}
                >
                  {dataCenters.map((dc) => (
                    <MenuItem key={dc.data_center} value={dc.data_center} style={{ fontSize: '12px' }} disabled={deploymentForm.data_centers.length >= 2 && !deploymentForm.data_centers.includes(dc.data_center)}>
                      <Checkbox checked={deploymentForm.data_centers.indexOf(dc.data_center) > -1} size="small" color="default" />
                      <ListItemText primary={dc.data_center} primaryTypographyProps={{ style: { fontSize: '12px' } }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>TX Chain Number</InputLabel>
                <Select value={deploymentForm.txchain_number} onChange={handleDeploymentChange('txchain_number')} label="TX Chain Number" disabled={deploymentForm.data_centers.length !== 2}>
                  {txChainNumbersWithStatus.map(({ number, busy }) => (
                    <MenuItem key={number} value={number} style={{ fontSize: '12px' }}>
                      {number}{busy && <span style={{ marginLeft: '8px', fontSize: '10px', color: '#e65100', fontWeight: 'bold' }}>(occupied)</span>}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {deploymentForm.data_centers.length !== 2 && (
                <Typography style={{ fontSize: '11px', color: '#888', marginTop: '-8px' }}>Select two data centers to see available TX chain numbers</Typography>
              )}
              {deploymentForm.data_centers.length === 2 && txChainNumbersWithStatus.length === 0 && (
                <Typography style={{ fontSize: '11px', color: '#f44336', marginTop: '-8px' }}>No common TX chain numbers between selected data centers</Typography>
              )}

              <FormControlLabel
                control={<Checkbox checked={deploymentForm.auto_deploy} onChange={handleDeploymentChange('auto_deploy')} size="small" color="default" />}
                label={<span style={{ fontSize: '12px' }}>Auto Deploy</span>}
              />
              <FormControlLabel
                control={<Checkbox checked={deploymentForm.create_udp} onChange={handleDeploymentChange('create_udp')} size="small" color="default" />}
                label={<span style={{ fontSize: '12px' }}>Create UDP Output</span>}
              />
            </>
          ) : (
            <>
              <TextField label="EID" variant="outlined" size="small" fullWidth value={channelForm.eid} onChange={handleChannelChange('eid')} placeholder="amzn1.dv.live.eid.xxxxx" required />

              <Autocomplete
                options={programGroupNames}
                value={channelForm.group_name || null}
                onChange={(event, newValue) =>
                  setChannelForm((prev) => ({ ...prev, group_name: newValue || '' }))
                }
                disabled={programGroupsLoading}
                size="small"
                ListboxProps={{ className: classes.autocompleteListbox }}
                PopperComponent={AutocompletePopper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Program Group"
                    variant="outlined"
                    placeholder={programGroupsLoading ? 'Loading...' : 'Type to search'}
                    required
                  />
                )}
              />

              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Data Center</InputLabel>
                <Select value={channelForm.data_center} onChange={handleChannelChange('data_center')} label="Data Center" disabled={dataCentersLoading}>
                  {dataCenters.map((dc) => (<MenuItem key={dc.data_center} value={dc.data_center} style={{ fontSize: '12px' }}>{dc.data_center}</MenuItem>))}
                </Select>
              </FormControl>

              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>TX Chain Name</InputLabel>
                <Select value={channelForm.tx_chain_name} onChange={handleChannelChange('tx_chain_name')} label="TX Chain Name" disabled={!channelForm.data_center}>
                  {txChainNamesWithStatus.map(({ name, busy }) => (
                    <MenuItem key={name} value={name} style={{ fontSize: '12px' }}>
                      {name}{busy && <span style={{ marginLeft: '8px', fontSize: '10px', color: '#e65100', fontWeight: 'bold' }}>(occupied)</span>}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!channelForm.data_center && (
                <Typography style={{ fontSize: '11px', color: '#888', marginTop: '-8px' }}>Select a data center to see available TX chains</Typography>
              )}
              {channelForm.data_center && txChainNamesWithStatus.length === 0 && (
                <Typography style={{ fontSize: '11px', color: '#f44336', marginTop: '-8px' }}>No TX chains in selected data center</Typography>
              )}

              {deploymentType === 'endpoint' && (
                <>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Region</InputLabel>
                    <Select value={channelForm.region} onChange={handleChannelChange('region')} label="Region">
                      {REGIONS.map((r) => (<MenuItem key={r} value={r} style={{ fontSize: '12px' }}>{r.toUpperCase()}</MenuItem>))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Stream Priority</InputLabel>
                    <Select value={channelForm.stream_priority} onChange={handleChannelChange('stream_priority')} label="Stream Priority">
                      {PRIORITIES.map((p) => (<MenuItem key={p} value={p} style={{ fontSize: '12px' }}>{p.toUpperCase()}</MenuItem>))}
                    </Select>
                  </FormControl>
                </>
              )}

              {deploymentType === 'fanout' && (
                <Typography style={{ fontSize: '11px', color: '#888' }}>
                  Creates one channel with an SRT output per EVIS ingress on the EID.
                  Region and stream priority are set per output by the backend.
                </Typography>
              )}

              <FormControlLabel
                control={<Checkbox checked={channelForm.auto_deploy} onChange={handleChannelChange('auto_deploy')} size="small" color="default" />}
                label={<span style={{ fontSize: '12px' }}>Auto Deploy</span>}
              />
              <FormControlLabel
                control={<Checkbox checked={channelForm.create_udp} onChange={handleChannelChange('create_udp')} size="small" color="default" />}
                label={<span style={{ fontSize: '12px' }}>Create UDP Output</span>}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} style={{ backgroundColor: loading ? '#ccc' : '#ff6f00', color: 'white' }}>
          {loading ? <CircularProgress size={20} style={{ color: 'white' }} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDeploymentForm;
